import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  loadApplication,
  loadAudit,
  pickUpApplication,
  releaseApplication,
  openDocument,
  clearCurrent,
  selectCurrentApplication,
  selectAudit,
  selectLoadingApplication,
  selectActing,
  selectUwError,
  selectActionError
} from '../../../redux/slices/underwriterSlice';
import { selectUser } from '../../../redux/slices/authSlice';
import StatusPill from '../../../components/statusPill';
import Modal from '../../../components/modal';
import DecisionPanel from './DeceisionPanel';
import { formatRupees } from '../../../utils/loanMath';
// import RuleAssessment from './RuleAssessment';
import { notify } from '../../../utils/notify';
import RiskScorePanel from './RiskScorePanel';
import RuleAssessment from './RuleAssessment';
import SystemRecommendation from './SystemRecommendation';
import AdditionalInfo from './AdditionalInfo';
import SystemAssistant from './SystemAssistant';
import './index.scss';

const LABELS = {
  gender: { MALE: 'Male', FEMALE: 'Female', OTHER: 'Other' },
  residenceType: { RENTED: 'Rented', OWNED: 'Owned', COMPANY_PROVIDED: 'Company provided' },
  employmentType: {
    SALARIED: 'Salaried',
    SELF_EMPLOYED: 'Self-employed',
    BUSINESS_OWNER: 'Business owner'
  },
  loanPurpose: {
    HOME_RENOVATION: 'Home renovation',
    MEDICAL_EMERGENCY: 'Medical emergency',
    EDUCATION: 'Education',
    VEHICLE_PURCHASE: 'Vehicle purchase',
    BUSINESS_EXPANSION: 'Business expansion',
    DEBT_CONSOLIDATION: 'Debt consolidation'
  },
  docType: {
    AADHAAR_CARD: 'Aadhaar card',
    PAN_CARD: 'PAN card',
    SALARY_SLIP: 'Salary slip',
    BANK_STATEMENT: 'Bank statement',
    ITR: 'Income tax return',
    FORM_16: 'Form 16'
  },
  action: {
    DRAFT_CREATED: 'Application started',
    APPLICATION_SUBMITTED: 'Submitted for review',
    PICKED_UP: 'Picked up for review',
    RELEASED: 'Returned to the queue',
    APPLICATION_WITHDRAWN: 'Withdrawn by applicant',
    DECISION_APPROVE: 'Approved',
    DECISION_APPROVE_WITH_CONDITIONS: 'Approved with conditions',
    DECISION_REJECT: 'Declined',
    DECISION_REQUEST_INFO: 'More information requested'
  }
};

const formatDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';

const formatDateTime = (iso) =>
  iso
    ? new Date(iso).toLocaleString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      })
    : '—';

const ageFrom = (iso) => {
  if (!iso) return null;
  const dob = new Date(iso);
  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const m = now.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) age -= 1;
  return age;
};

const DataBlock = ({ title, rows }) => (
  <section className="data-block">
    <h2 className="data-block__title">{title}</h2>
    <dl className="data-block__list">
      {rows.map(({ label, value, mono, flag }) => (
        <div key={label} className="data-row">
          <dt className="data-row__label">{label}</dt>
          <dd
            className={`data-row__value ${mono ? 'numeric' : ''} ${flag ? `data-row__value--${flag}` : ''}`}>
            {value ?? '—'}
          </dd>
        </div>
      ))}
    </dl>
  </section>
);

DataBlock.propTypes = {
  title: PropTypes.string.isRequired,
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.node,
      mono: PropTypes.bool,
      flag: PropTypes.string
    })
  ).isRequired
};

const ReviewPage = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const application = useSelector(selectCurrentApplication);
  const audit = useSelector(selectAudit);
  const loading = useSelector(selectLoadingApplication);
  const acting = useSelector(selectActing);
  const error = useSelector(selectUwError);
  const actionError = useSelector(selectActionError);
  const user = useSelector(selectUser);

  const [openingDoc, setOpeningDoc] = useState(null);
  const [engineMode, setEngineMode] = useState('combined'); // 'rule' | 'ai' | 'combined'
  const [decisionOpen, setDecisionOpen] = useState(false);

  useEffect(() => {
    dispatch(loadApplication(id));
    dispatch(loadAudit(id));
    return () => dispatch(clearCurrent());
  }, [dispatch, id]);

  const handleOpenDocument = async (documentId) => {
    setOpeningDoc(documentId);
    const result = await dispatch(openDocument(documentId));
    setOpeningDoc(null);

    if (openDocument.fulfilled.match(result)) {
      window.open(result.payload, '_blank', 'noopener,noreferrer');
    }
  };

  if (loading && !application) {
    return (
      <div className="review-loading">
        <span className="review-loading__spinner" aria-hidden="true" />
        <span>Loading application…</span>
      </div>
    );
  }

  if (error && !application) {
    return (
      <div className="review-loading">
        <p className="form-error">{error}</p>
        <button type="button" className="btn btn--ghost" onClick={() => navigate('/review')}>
          ← Back to queue
        </button>
      </div>
    );
  }

  if (!application) return null;

  const isMine = application.assignedToId === user?.id;
  const claimedByOther = application.assignedToId && !isMine;
  const decided = Boolean(application.decisionAt);
  const foir = Number(application.snapshotFoir);
  const foirFlag = !Number.isFinite(foir) ? null : foir > 50 ? 'high' : foir > 40 ? 'watch' : 'ok';
  const age = ageFrom(application.dateOfBirth);

  console.log('Risk assessment:', application.riskAssessment);
  console.log('Risk assessment:', application);
  return (
    <div className="review-page">
      <button type="button" className="review-back" onClick={() => navigate('/review')}>
        ← Queue
      </button>

      <header className="review-header">
        <div className="review-header__main">
          <h1 className="review-header__name">
            {[application.firstName, application.lastName].filter(Boolean).join(' ') ||
              'Unnamed applicant'}
          </h1>
          <div className="review-header__meta">
            <span className="numeric">{application.referenceNo}</span>
            <span aria-hidden="true">·</span>
            <span>Submitted {formatDateTime(application.submittedAt)}</span>
          </div>
        </div>

        <div className="review-header__side">
          <StatusPill status={application.status} />

          {claimedByOther && (
            <span className="review-claimed">
              Being reviewed by {application.assignedTo?.username}
            </span>
          )}

          {!application.assignedToId && !decided && (
            <button
              type="button"
              className="btn btn--primary"
              onClick={async () => {
                const result = await dispatch(pickUpApplication(id));
                if (pickUpApplication.fulfilled.match(result)) {
                  notify.success('Application picked up for review');
                  setDecisionOpen(true);
                } else {
                  notify.error(result.payload);
                }
              }}
              disabled={acting}>
              {acting ? 'Picking up…' : 'Pick up for review'}
            </button>
          )}

          {isMine && !decided && (
            <button
              type="button"
              className="btn btn--ghost"
              onClick={async () => {
                const result = await dispatch(releaseApplication(id));
                if (releaseApplication.fulfilled.match(result)) {
                  notify.info('Returned to the queue');
                } else {
                  notify.error(result.payload);
                }
              }}
              disabled={acting}>
              Return to queue
            </button>
          )}
        </div>
      </header>

      {actionError && (
        <p className="form-error" role="alert">
          {actionError}
        </p>
      )}

      <div className="review-figures">
        <div className="figure-tile">
          <span className="figure-tile__label">Requested</span>
          <span className="figure-tile__value numeric">{formatRupees(application.loanAmount)}</span>
          <span className="figure-tile__sub numeric">{application.tenureMonths} months</span>
        </div>

        <div className="figure-tile">
          <span className="figure-tile__label">Monthly EMI</span>
          <span className="figure-tile__value numeric">
            {formatRupees(application.snapshotEmi)}
          </span>
          <span className="figure-tile__sub numeric">at {application.snapshotRatePct}% p.a.</span>
        </div>

        <div className={`figure-tile figure-tile--${foirFlag ?? 'none'}`}>
          <span className="figure-tile__label">FOIR</span>
          <span className="figure-tile__value numeric">
            {Number.isFinite(foir) ? `${foir.toFixed(1)}%` : '—'}
          </span>
          <span className="figure-tile__sub">
            {foirFlag === 'high'
              ? 'Above the 50% policy limit'
              : foirFlag === 'watch'
                ? 'Approaching the limit'
                : 'Within policy'}
          </span>
        </div>

        <div className="figure-tile">
          <span className="figure-tile__label">Declared income</span>
          <span className="figure-tile__value numeric">
            {formatRupees(application.monthlyIncome)}
          </span>
          <span className="figure-tile__sub">per month</span>
        </div>
      </div>

      {/*
        Placeholder for credit bureau and risk scoring. These need CIBIL
        integration and an ML service — neither exists yet. The layout
        reserves the space so they slot in without a redesign.
      */}

      <SystemAssistant />

      {/* <RuleAssessment assessment={application.assessment} /> */}
      {/* <RiskScorePanel assessment={application.riskAssessment} /> */}
      <div className="engine-mode">
        <div className="engine-mode__left">
          <span className="engine-mode__label">Engine Mode</span>
          <div className="engine-mode__tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={engineMode === 'rule'}
              className={`engine-mode__tab ${engineMode === 'rule' ? 'engine-mode__tab--active' : ''}`}
              onClick={() => setEngineMode('rule')}>
              Rule Engine Only
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={engineMode === 'ai'}
              className={`engine-mode__tab ${engineMode === 'ai' ? 'engine-mode__tab--active engine-mode__tab--ai' : ''}`}
              onClick={() => setEngineMode('ai')}>
              AI Engine Only
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={engineMode === 'combined'}
              className={`engine-mode__tab ${engineMode === 'combined' ? 'engine-mode__tab--active engine-mode__tab--combined' : ''}`}
              onClick={() => setEngineMode('combined')}>
              Combined (Rule + AI)
            </button>
          </div>
        </div>

        <div className="engine-mode__right">
          <span className="engine-mode__status">
            <span className={`engine-mode__dot engine-mode__dot--${engineMode}`} />
            {engineMode === 'rule' && 'Rule Engine active · Policy Rules'}
            {engineMode === 'ai' && 'AI Engine active · Amazon Nova Lite'}
            {engineMode === 'combined' && 'Combined active · Rules + AI'}
          </span>
        </div>
      </div>

      <SystemRecommendation
        assessment={application.assessment}
        risk={application.riskAssessment}
        engineMode={engineMode}
      />

      {/* Rule engine — shown in rule and combined modes */}
      {(engineMode === 'rule' || engineMode === 'combined') && (
        <RuleAssessment assessment={application.assessment} />
      )}

      {/* AI risk — shown in ai and combined modes */}
      {(engineMode === 'ai' || engineMode === 'combined') && (
        <>
          <RiskScorePanel assessment={application.riskAssessment} />
          <AdditionalInfo application={application} />
        </>
      )}

      <section className="pending-panel">
        <span className="pending-panel__badge">Not yet active</span>
        <span className="pending-panel__text">
          Credit checks (CIBIL, NPA, enquiries) require bureau integration and are not included in
          this assessment. These activate in a future phase.
        </span>
      </section>

      <div className="review-columns">
        <div className="review-columns__main">
          <DataBlock
            title="Applicant"
            rows={[
              {
                label: 'Date of birth',
                value: `${formatDate(application.dateOfBirth)}${age ? ` · age ${age}` : ''}`
              },
              { label: 'Gender', value: LABELS.gender[application.gender] },
              { label: 'Mobile', value: application.mobileNumber, mono: true },
              { label: 'Email', value: application.contactEmail },
              {
                label: 'Address',
                value: [
                  application.currentAddress,
                  application.city,
                  application.state,
                  application.pinCode
                ]
                  .filter(Boolean)
                  .join(', ')
              },
              { label: 'Residence', value: LABELS.residenceType[application.residenceType] }
            ]}
          />

          <DataBlock
            title="Identity and employment"
            rows={[
              {
                label: 'Aadhaar',
                value: application.aadhaarLast4 ? `XXXX XXXX ${application.aadhaarLast4}` : null,
                mono: true
              },
              { label: 'PAN', value: application.panNumber, mono: true },
              { label: 'Voter ID', value: application.voterId, mono: true },
              { label: 'Passport', value: application.passportNo, mono: true },
              { label: 'Employment', value: LABELS.employmentType[application.employmentType] },
              { label: 'Employer', value: application.employerName },
              {
                label: 'Years there',
                value:
                  application.yearsAtEmployer != null ? `${application.yearsAtEmployer}` : null,
                mono: true
              }
            ]}
          />

          <DataBlock
            title="Loan request"
            rows={[
              { label: 'Purpose', value: LABELS.loanPurpose[application.loanPurpose] },
              {
                label: 'EMI date',
                value: application.preferredEmiDay
                  ? `${application.preferredEmiDay} of each month`
                  : null
              },
              {
                label: 'Consent given',
                value: application.consentAt
                  ? formatDateTime(application.consentAt)
                  : 'Not recorded'
              }
            ]}
          />

          <section className="data-block">
            <h2 className="data-block__title">Documents</h2>

            {application.documents.length === 0 ? (
              <p className="doc-empty">No documents uploaded.</p>
            ) : (
              <ul className="doc-list-uw">
                {application.documents.map((doc) => (
                  <li key={doc.id} className="doc-list-uw__item">
                    <span className="doc-list-uw__type">
                      {LABELS.docType[doc.docType] ?? doc.docType}
                    </span>
                    <span className="doc-list-uw__name">{doc.originalName}</span>
                    <span className="doc-list-uw__date">{formatDate(doc.uploadedAt)}</span>
                    <button
                      type="button"
                      className="doc-list-uw__open"
                      onClick={() => handleOpenDocument(doc.id)}
                      disabled={openingDoc === doc.id}>
                      {openingDoc === doc.id ? 'Opening…' : 'Open'}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        <aside className="review-columns__side">
          {isMine && !decided && (
            <section className="decision-cta">
              <h2 className="decision-cta__title">Your decision</h2>
              <p className="decision-cta__text">
                You have picked this up. Review the assessment and record your call.
              </p>
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => setDecisionOpen(true)}>
                Record decision →
              </button>
            </section>
          )}

          {decided && (
            <section className="decided-block">
              <h2 className="decided-block__title">Decision recorded</h2>
              <StatusPill status={application.status} />

              {application.approvedAmount && (
                <div className="decided-block__row">
                  <span className="decided-block__label">Approved amount</span>
                  <span className="decided-block__value numeric">
                    {formatRupees(application.approvedAmount)}
                  </span>
                </div>
              )}

              {application.conditionsText && (
                <div className="decided-block__section">
                  <span className="decided-block__label">Conditions</span>
                  <p className="decided-block__text">{application.conditionsText}</p>
                </div>
              )}

              <div className="decided-block__section">
                <span className="decided-block__label">Remarks</span>
                <p className="decided-block__text">{application.decisionRemarks}</p>
              </div>

              <span className="decided-block__meta">{formatDateTime(application.decisionAt)}</span>
            </section>
          )}

          <section className="audit-block">
            <h2 className="audit-block__title">History</h2>
            <ol className="audit-list">
              {audit.map((entry) => (
                <li key={entry.id} className="audit-item">
                  <span className="audit-item__dot" aria-hidden="true" />
                  <div className="audit-item__body">
                    <span className="audit-item__action">
                      {LABELS.action[entry.action] ?? entry.action}
                    </span>
                    <span className="audit-item__meta">
                      {entry.actor?.username ?? 'System'} · {formatDateTime(entry.createdAt)}
                    </span>
                    {entry.notes && <p className="audit-item__notes">{entry.notes}</p>}
                  </div>
                </li>
              ))}
            </ol>
          </section>
        </aside>
      </div>

      {decisionOpen && (
        <Modal title="Record your decision" onClose={() => setDecisionOpen(false)}>
          <DecisionPanel application={application} onRecorded={() => setDecisionOpen(false)} />
        </Modal>
      )}
    </div>
  );
};

export default ReviewPage;
